// src/components/modals/ViewSaleModal.jsx
import { useState, useEffect, useCallback } from 'react'
import { X, Receipt, User, MapPin, Calendar, Clock, CreditCard, Package, FileText } from 'lucide-react'
import { Button } from '../ui/Button'
import InlineLoader from '../ui/InlineLoader'
import SalesService from '../../services/salesService'
import { SalesModel } from '../../utils/salesModel'
import { EnhancedReceiptGenerator } from '../../utils/enhancedReceiptGenerator'
import { HTMLReceiptGenerator } from '../../utils/htmlReceiptGenerator'
import { SimpleItemEnhancer } from '../../utils/simpleItemEnhancer'

const ViewSaleModal = ({ isOpen, onClose, sale: propSale, saleId }) => {
  console.log('ViewSaleModal props:', { isOpen, onClose: !!onClose, propSale: !!propSale, saleId });
  
  const [sale, setSale] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [enhancedItems, setEnhancedItems] = useState([])
  const [itemsLoading, setItemsLoading] = useState(false)

  // Enhance items with product details
  const enhanceItems = useCallback(async (items) => {
    if (!items || !Array.isArray(items)) return []
    
    try {
      setItemsLoading(true)
      console.log('🔍 Starting item enhancement process:', items)
      
      // First try simple enhancement with embedded data (immediate)
      const simpleEnhanced = SimpleItemEnhancer.enhanceItemsWithEmbeddedData(items)
      console.log('✅ Simple enhanced items:', simpleEnhanced)
      setEnhancedItems(simpleEnhanced)
      
      // Debug the structure
      SimpleItemEnhancer.debugItemStructure(items)
      
      // Check if we still have unknown items that need API enhancement
      const hasUnknownItems = simpleEnhanced.some(item => 
        item.name.includes('Item #') || item.name === 'Unknown Item'
      )
      
      if (hasUnknownItems) {
        console.log('🔄 Some items still unknown, trying API enhancement...')
        const apiEnhanced = await EnhancedReceiptGenerator.enhanceItemsWithProductDetails(items)
        console.log('✅ API enhanced items:', apiEnhanced)
        setEnhancedItems(apiEnhanced)
        return apiEnhanced
      }
      
      return simpleEnhanced
    } catch (error) {
      console.error('❌ Failed to enhance items:', error)
      // Fallback to simple enhancement
      const fallbackEnhanced = SimpleItemEnhancer.enhanceItemsWithEmbeddedData(items)
      setEnhancedItems(fallbackEnhanced)
      return fallbackEnhanced
    } finally {
      setItemsLoading(false)
    }
  }, [])

  // PDF Download Handler
  const handleDownloadPDF = useCallback(async () => {
    if (!sale) return

    try {
      console.log('📄 Generating PDF receipt...')
      
      // Use enhanced items if available, fallback to simple enhancement
      let itemsToUse = enhancedItems.length > 0 ? enhancedItems : 
                       SimpleItemEnhancer.enhanceItemsWithEmbeddedData(sale.items)
      
      const receipt = new EnhancedReceiptGenerator(sale, itemsToUse)
      await receipt.generatePDF()
      
      console.log('✅ PDF generated successfully')
    } catch (error) {
      console.error('❌ Failed to generate PDF:', error)
      alert('Failed to generate PDF. Please try again.')
    }
  }, [sale, enhancedItems])

  // Print Receipt Handler
  const handlePrintReceipt = useCallback(async () => {
    if (!sale) return

    try {
      console.log('🖨️ Generating print receipt...')
      
      // Use enhanced items if available, fallback to simple enhancement  
      let itemsToUse = enhancedItems.length > 0 ? enhancedItems :
                       SimpleItemEnhancer.enhanceItemsWithEmbeddedData(sale.items)
                       
      const printGenerator = new HTMLReceiptGenerator(sale, itemsToUse)
      printGenerator.print()
      
      console.log('✅ Print dialog opened')
    } catch (error) {
      console.error('❌ Failed to print receipt:', error)
      alert('Failed to print receipt. Please try again.')
    }
  }, [sale, enhancedItems])

  // Load sale details
  const loadSaleDetails = useCallback(async () => {
    // If sale data is passed as prop, use it directly
    if (propSale) {
      console.log('🎯 Using prop sale data:', propSale)
      setSale(propSale)
      
      // Enhance the items from prop data
      if (propSale.items) {
        await enhanceItems(propSale.items)
      }
      return
    }
    
    // Otherwise, fetch from API
    if (!saleId) return

    try {
      setLoading(true)
      setError(null)
      
      console.log('🔍 Loading sale details for ID:', saleId)
      const response = await SalesService.getSaleById(saleId)
      console.log('📦 Raw API response:', response)
      
      // Handle different possible response structures
      let saleData
      if (response.success && response.success.data) {
        saleData = response.success.data
        console.log('✅ Using response.success.data:', saleData)
      } else if (response.data) {
        saleData = response.data
        console.log('✅ Using response.data:', saleData)
      } else {
        saleData = response
        console.log('✅ Using direct response:', saleData)
      }
      
      // Validate that we have data
      if (!saleData) {
        console.error('❌ No sale data received:', saleData)
        throw new Error('No sale data received from API')
      }
      
      console.log('🧪 Sale data structure:', {
        hasId: !!saleData.id,
        has_id: !!saleData._id,
        keys: Object.keys(saleData || {}),
        sampleData: saleData
      })
      
      // Format sale data for display directly
      const formattedSale = SalesModel.formatSaleForDisplay(saleData)
      console.log('🎨 Formatted sale data:', formattedSale)
      
      setSale(formattedSale)
      
      // Enhance the items after setting the sale
      if (formattedSale.items) {
        await enhanceItems(formattedSale.items)
      }
      
    } catch (error) {
      console.error('❌ Failed to load sale details:', error)
      setError(`Failed to load sale details: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }, [saleId, propSale, enhanceItems])

  useEffect(() => {
    if (isOpen) {
      loadSaleDetails()
    }
  }, [isOpen, loadSaleDetails])

  const handleClose = () => {
    setSale(null)
    setError(null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-full">
              <Receipt className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Sale Details</h2>
              {sale && (
                <p className="text-sm text-gray-600">Order #{sale.orderNumber}</p>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            style={{ color: '#9CA3AF !important' }}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="py-8">
              <InlineLoader message="Loading sale details..." />
            </div>
          ) : error ? (
            <div className="py-8 text-center">
              <div className="text-red-500 text-lg mb-2">❌</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Sale</h3>
              <p className="text-gray-500 mb-4">{error}</p>
              <Button onClick={loadSaleDetails} variant="outline">
                Try Again
              </Button>
            </div>
          ) : sale ? (
            <div className="space-y-6">
              {/* Sale Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-600">Date</span>
                  </div>
                  <p className="text-sm text-gray-900">{sale.orderDateFormatted}</p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <Clock className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-600">Time</span>
                  </div>
                  <p className="text-sm text-gray-900">{sale.orderTimeFormatted}</p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <Package className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-600">Type</span>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${sale.orderTypeConfig.badgeClass}`}>

                    {sale.orderTypeConfig.label}
                  </span>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <CreditCard className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-600">Payment</span>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${sale.paymentStatusConfig.badgeClass}`}>

                    {sale.paymentStatusConfig.label}
                  </span>
                </div>
              </div>

              {/* Customer & Branch Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Customer Information */}
                <div className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-2 mb-3">
                    <User className="h-5 w-5 text-gray-600" />
                    <h3 className="font-semibold text-gray-900">Customer Information</h3>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm font-medium text-gray-600">Name:</span>
                      <span className="text-sm text-gray-900 ml-2">{sale.customerName}</span>
                    </div>
                    {sale.customerMobile && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Mobile:</span>
                        <span className="text-sm text-gray-900 ml-2">{sale.customerMobile}</span>
                      </div>
                    )}
                    {sale.customer.email && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Email:</span>
                        <span className="text-sm text-gray-900 ml-2">{sale.customer.email}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Branch Information */}
                <div className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-2 mb-3">
                    <MapPin className="h-5 w-5 text-gray-600" />
                    <h3 className="font-semibold text-gray-900">Branch Information</h3>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm font-medium text-gray-600">Branch:</span>
                      <span className="text-sm text-gray-900 ml-2">{sale.branchName}</span>
                    </div>
                    {sale.branch.address && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Address:</span>
                        <span className="text-sm text-gray-900 ml-2">{sale.branch.address}</span>
                      </div>
                    )}
                    {sale.createdBy?.name && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Served by:</span>
                        <span className="text-sm text-gray-900 ml-2">{sale.createdBy.name}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Items */}
              <div className="border border-gray-200 rounded-lg">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                  <h3 className="font-semibold text-gray-900">
                    Order Items 
                    {itemsLoading && (
                      <span className="ml-2 text-sm text-blue-600">
                        (Loading product details...)
                      </span>
                    )}
                  </h3>
                </div>
                <div className="p-4">
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 font-medium text-gray-600">Item</th>
                          <th className="text-center py-2 font-medium text-gray-600">Qty</th>
                          <th className="text-right py-2 font-medium text-gray-600">Unit Price</th>
                          <th className="text-right py-2 font-medium text-gray-600">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(enhancedItems.length > 0 ? enhancedItems : sale.items).map((item, index) => {
                          const displayItem = enhancedItems.length > 0 ? item : item
                          return (
                            <tr key={index} className="border-b border-gray-100">
                              <td className="py-2">
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {displayItem.name || displayItem.productName || `Item #${index + 1}`}
                                  </p>
                                  {displayItem.description && (
                                    <p className="text-sm text-gray-500">{displayItem.description}</p>
                                  )}
                                  {displayItem.category && (
                                    <p className="text-xs text-blue-600">Category: {displayItem.category}</p>
                                  )}
                                  {displayItem.sku && (
                                    <p className="text-xs text-gray-400">SKU: {displayItem.sku}</p>
                                  )}
                                </div>
                              </td>
                              <td className="text-center py-2 text-gray-900">{displayItem.quantity}</td>
                              <td className="text-right py-2 text-gray-900">
                                {SalesModel.formatCurrency(displayItem.unitPrice || displayItem.price)}
                              </td>
                              <td className="text-right py-2 font-medium text-gray-900">
                                {SalesModel.formatCurrency((displayItem.unitPrice || displayItem.price) * displayItem.quantity)}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Payment Summary */}
              <div className="border border-gray-200 rounded-lg">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                  <h3 className="font-semibold text-gray-900">Payment Summary</h3>
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="text-gray-900">{sale.subtotalFormatted}</span>
                  </div>
                  {sale.discount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Discount:</span>
                      <span className="text-red-600">-{sale.discountFormatted}</span>
                    </div>
                  )}
                  {sale.tax > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tax:</span>
                      <span className="text-gray-900">{sale.taxFormatted}</span>
                    </div>
                  )}
                  <div className="border-t pt-2 flex justify-between font-semibold text-lg">
                    <span className="text-gray-900">Total:</span>
                    <span className="text-gray-900">{sale.totalFormatted}</span>
                  </div>
                  <div className="mt-4 pt-2 border-t">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Payment Method:</span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${sale.paymentMethodConfig.badgeClass}`}>

                        {sale.paymentMethodConfig.label}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {sale.notes && (
                <div className="border border-gray-200 rounded-lg">
                  <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center space-x-2">
                      <FileText className="h-4 w-4 text-gray-600" />
                      <h3 className="font-semibold text-gray-900">Notes</h3>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-gray-700">{sale.notes}</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 text-center">
              <div className="text-gray-400 text-lg mb-2">❓</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Sale Not Found</h3>
              <p className="text-gray-500">The requested sale could not be found.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200">
          <div className="flex space-x-2">
            {/* {sale && (
              <>
                <Button
                  onClick={() => handleDownloadPDF()}
                  variant="outline"
                  className="!bg-blue-50 hover:!bg-blue-100 !text-blue-700 !border-blue-300"
                >
                  📄 Download PDF
                </Button>
                <Button
                  onClick={() => handlePrintReceipt()}
                  variant="outline" 
                  className="!bg-green-50 hover:!bg-green-100 !text-green-700 !border-green-300"
                >
                  🖨️ Print Receipt
                </Button>
              </>
            )} */}
          </div>
          
          <Button
            onClick={handleClose}
            variant="secondary"
            className="!bg-gray-200 hover:!bg-gray-300 !text-gray-700"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ViewSaleModal